import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

const Circles = lazy(() => import('@/assets/images/circles.svg?react'));

export function EmptyStateCircles(emptyStateCirclesProps: ComponentProps<'svg'>) {
  const { className, ...props } = emptyStateCirclesProps;

  return (
    <ErrorBoundary fallback={<EmptyStateCirclesFallback className={className} />}>
      <Suspense fallback={<EmptyStateCirclesFallback className={className} />}>
        <Circles data-slot="empty-state-circles" className={cn('absolute top-0 left-2/4 -z-1 flex aspect-square w-fit max-w-54 -translate-x-2/4 opacity-50 md:max-w-66', className)} {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

export function EmptyStateCirclesFallback(emptyStateCirclesFallbackProps: ComponentProps<'div'>) {
  const { className, ...props } = emptyStateCirclesFallbackProps;

  return (
    <div
      aria-hidden="true"
      role="presentation"
      data-slot="empty-state-circles"
      className={cn('bg-accent/65 absolute top-0 left-2/4 -z-1 flex aspect-square w-fit max-w-54 -translate-x-2/4 rounded-full opacity-50 md:max-w-66', className)}
      {...props}
    />
  );
}

export function EmptyState(emptyStateProps: ComponentProps<'div'>) {
  const { className, ...props } = emptyStateProps;

  return (
    <div
      data-slot="empty-state"
      className={cn('relative flex flex-col items-center gap-4 has-[>[data-slot="empty-state-circles"]]:pt-24 md:has-[>[data-slot="empty-state-circles"]]:pt-32', className)}
      {...props}
    />
  );
}

export function EmptyStateContent(emptyStateContentProps: ComponentProps<'div'>) {
  const { className, ...props } = emptyStateContentProps;

  return <div data-slot="empty-state-content" className={cn('z-10 flex flex-col gap-1 p-4', className)} {...props} />;
}

export function EmptyStateFooter(emptyStateFooterProps: ComponentProps<'div'>) {
  const { className, ...props } = emptyStateFooterProps;

  return <div data-slot="empty-state-footer" className={cn('flex w-full justify-center gap-3 pt-5', className)} {...props} />;
}

export function EmptyStateTitle(emptyStateTitleProps: ComponentProps<'h2'>) {
  const { className, ...props } = emptyStateTitleProps;

  return <h2 data-slot="empty-state-title" className={cn('text-foreground text-center text-xl font-semibold md:text-2xl', className)} {...props} />;
}

export function EmptyStateDescription(emptyStateDescriptionProps: ComponentProps<'p'>) {
  const { className, ...props } = emptyStateDescriptionProps;

  return <p data-slot="empty-state-description" className={cn('text-muted-foreground text-center text-xs md:text-sm', className)} {...props} />;
}
